import * as React from "react";
import Box from "../../../../parts/Box";
import * as Messages from "../../../../Messages";
import MutationTable from "./MutationTable";
import MutationTextualInput from "./MutationTextualInput";
import MutationTableModalButton from "./MutationTableModalButton";
import { detectOneAmbiguousMutation, IAmbiguousMutation, IMatchingIsoform } from "../../mutation-ambiguity";
import { uniprotLink } from "../../../../utils/toolsReact";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInputContext } from "../../context/input-context";

function MutationBoxComponent() {

  // Context:

  const {
    targetSelected,
    sequence,
    level,
    mutations, setMutations,
    multiplePos, multipleUnits,
    codonMutations,
  } = useInputContext();

  const mutatedPositions = useMemo(() => {
    return mutations.map(({ position }) => position);
  }, [mutations]);

  // State:
  const [mutationOnceSelected, setMutationOnceSelected] = useState(false);

  // Custom hooks:
  const ambiguousMutation = useAmbiguousMutation();

  // Callbacks:

  const clearPosition = useCallback((position: number | number[]) => {
    const posArr = Array.isArray(position) ? position : [position];
    setMutations(mutations => mutations.filter(m => !posArr.includes(m.position)));
  }, [setMutations]);

  // Effects:

  useEffect(() => {
    if (mutations.length > 0) {
      setMutationOnceSelected(true);
    }
  }, [mutations]);

  // Render section:

  function renderAmbiguousMutationIsoform(i: IMatchingIsoform, selected = false) {
    return (
      <tr key={i.target.uniprot}>
        <td style={{ paddingRight: '1rem' }}>Isoform {uniprotLink(i.target.uniprot)}{selected ? ' (selected)' : ''}</td>
        <td style={{ fontFamily: "'Courier New', Courier, monospace", letterSpacing: '3px' }}>
          {i.neighbourhood[0]}<strong>{i.neighbourhood[1]}</strong>{i.neighbourhood[2]}
        </td>
      </tr>
    )
  }

  const multiple = multiplePos || multipleUnits;
  const boxTitle = multiple ? "Select mutations" : "Select mutation";

  const essentialResidueMutated = targetSelected !== null && mutatedPositions.some(p => targetSelected.essentialResiduePositions.includes(p));

  // check whether mutated residue has structural information
  const structResidues = targetSelected?.structResidues;
  const noStructForMutatedResidue: boolean = structResidues ? (
    mutations.some(m => !structResidues.includes(m.position))
  ) : false;

  return (
    <Box title={boxTitle}>
      <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
        <MutationTextualInput/>
        <MutationTableModalButton/>
      </div>
      {(mutationOnceSelected || mutations.length > 0) && (
        <MutationTable
          targetName={targetSelected?.code ?? ""}
          uniprot={targetSelected?.uniprot ?? ""}
          level={level}
          sequence={sequence}
          mutations={mutations}
          codonMutations={codonMutations}
          clearPosition={clearPosition}
          emptyMsgMultiple={multiple}
        />
      )}
      {essentialResidueMutated && (
        <div style={{ marginTop: '1rem' }}>
          {Messages.MUTATION_AT_ER()}
        </div>
      )}
      {noStructForMutatedResidue && (
        <div style={{ marginTop: '1rem' }}>
          {Messages.MUTATION_NO_STRUCT_FOR_RESIDUE()}
        </div>
      )}
      {ambiguousMutation && (
        <div style={{ marginTop: '1rem' }}>
          {Messages.AMBIGUOUS_MUTATION()}
          <table><tbody>
          {renderAmbiguousMutationIsoform(ambiguousMutation.selectedIsoform, true)}
          {ambiguousMutation.alternativeIsoforms.map(i => renderAmbiguousMutationIsoform(i))}
          </tbody></table>
        </div>
      )}
    </Box>
  )
}

function MutationBoxComponentWrapper() {
  // MutationBoxComponent has internal state whether any mutations have been selected already.
  // We should not use effect (with sequence as dependency) to reset it, because it would be
  // in conflict with the effect to setting it to true.
  // When the Load example button is used, the component is mounted with a mutation filled in,
  // and the effects would do the opposite of each other.
  // Therefore, we reset the state by re-mounting the component through a key.
  const {targetSelected} = useInputContext();
  const key = targetSelected?.codeAscii ?? '';
  return <MutationBoxComponent key={key} />
}

function useAmbiguousMutation() {
  const {
    rowSelected,
    targetSelected,
    sequence,
    mutations,
  } = useInputContext();
  const targetsOfSelectedRow = rowSelected?.proteins;
  return useMemo<IAmbiguousMutation | null>(() => {
    if (targetsOfSelectedRow && targetSelected) {
      for (const m of mutations) {
        const am = detectOneAmbiguousMutation(m.position, sequence, targetsOfSelectedRow, targetSelected);
        if (am) return am;
      }
    }
    return null;
  }, [mutations, sequence, targetsOfSelectedRow, targetSelected]);
}

export default MutationBoxComponentWrapper;
